<template>
  <TemplateBase>
    <div class="hidden"><!-- This health check is used by Exonet.  Do NOT remove it from the homepage! -->app-health-check:{{ site }}</div>
    <main class="relative z-0 col-span-full row-start-2">
      <!-- Header -->
      <MoleculeHeaderAnimalHome class="header-margin relative 2xl:-mt-10 -mt-4 sm:-mt-6 lg:-mt-8" :title="page.title" :subtitle="page.subtitle" :image="page.image" />
      <MoleculeAnimalCategories
        :translation-group="site"
        :categories="page.animalCategories"
        :animal-counts="animalCounts"
        class="container relative z-10 mb-16 mx-auto px-4 2xl:px-56 md:px-6 lg:px-8 xl:px-12"
      />

      <section v-if="page.highlightedAnimals.length" class="container relative z-10 mb-16 mx-auto 2xl:px-56 px-6 sm:px-8">
        <div class="grid grid-cols-12 mb-6">
          <h2 class="col-span-full mb-2 text-2xl font-bold leading-none uppercase md:col-span-7 md:mb-4 md:text-3xl text-black">
            {{ t_('latest-animals-title') }}
          </h2>
          <div class="prose prose--app col-span-full max-w-none md:col-span-7" v-html="t_('latest-animals-description')" />
        </div>
        <MoleculeCarousel :slides="page.highlightedAnimals" background="bg-app-green-200" type="slider">
          <template #slide="{ slide }">
            <OrganismLoaderFromAnimalId v-slot="{ animal }" :animal-id="slide">
              <MoleculeCarouselSlideAnimal :slide="animal" :translation-group="animal.animalType" />
            </OrganismLoaderFromAnimalId>
          </template>
        </MoleculeCarousel>
      </section>

      <MoleculeMatrixBanner
        v-if="page.bannerImage && page.bannerTitle"
        :title="page.bannerTitle"
        :introduction="page.bannerText"
        :image="page.bannerImage"
        :primary-button-label="page.buttonTitle"
        :primary-button-url="page.buttonUrl"
        :secondary-button-label="page.secondaryButtonTitle"
        :secondary-button-url="page.secondaryButtonUrl"
        class="col-span-full mb-12 mt-16 sm:mb-16 sm:mt-16"
      />

      <section v-if="page.misfitAnimals.length" class="container relative z-10 mb-16 mx-auto 2xl:px-56 px-6 sm:px-8">
        <div class="grid grid-cols-12 mb-6">
          <h2 class="col-span-full mb-2 text-2xl font-bold leading-none uppercase md:col-span-7 md:mb-4 md:text-3xl text-black">
            {{ t_('misfit-animals-title') }}
          </h2>
          <div class="prose prose--app col-span-full max-w-none md:col-span-7" v-html="t_('misfit-animals-description')" />
        </div>
        <MoleculeCarousel :slides="page.misfitAnimals" background="bg-app-green-200" type="slider">
          <template #slide="{ slide }">
            <OrganismLoaderFromAnimalId v-slot="{ animal }" :animal-id="slide">
              <MoleculeCarouselSlideAnimal :slide="animal" :translation-group="animal.animalType" />
            </OrganismLoaderFromAnimalId>
          </template>
        </MoleculeCarousel>
      </section>

      <section v-if="page.animalSuccesses.length" class="article-overview-container relative z-10 mb-16 mx-auto md:container md:px-8 lg:mb-24">
        <MoleculeCollage :label="page.animalSuccessTitle" :subtitle="page.animalSuccessSubtitle" :entries="page.animalSuccesses" class="article-overview-container__molecule">
          <template #large="{ entry }">
            <MoleculeCardOverviewCover
              :entry-type="t_('animal-success-label')"
              :entry-url="entry.url"
              :entry-title="entry.nameAnimal"
              :subtitle="entry.message"
              :image="entry.image"
              :collage-large="true"
            />
          </template>
          <template #medium="{ entry }">
            <MoleculeCardOverviewCover
              :entry-type="t_('animal-success-label')"
              :entry-url="entry.url"
              :entry-title="entry.nameAnimal"
              :subtitle="entry.message"
              :image="entry.image"
              :collage-medium="true"
            />
          </template>
        </MoleculeCollage>

        <div v-if="page.successButtonTitle && page.successButtonUrl" class="group flex mt-3">
          <AtomLink :url="page.successButtonUrl" class="flex items-center ml-auto">
            <span class="text-black font-semibold border-b border-solid border-app-green">
              {{ page.successButtonTitle }}
            </span>
            <AtomIconArrowRight class="ml-2 w-3 h-3 text-app-green transform group-hover:translate-x-1 transition duration-200" />
          </AtomLink>
        </div>
      </section>

      <section v-if="page.homeMissionTitle || page.homeMissionEmbed" class="container relative z-10 mb-16 mx-auto 2xl:px-56 px-6 sm:px-8">
        <MoleculeMission
          :embed-title="page.homeMissionEmbedTitle"
          :embed="page.homeMissionEmbed"
          :subtitle="page.homeMissionTitle"
          :description="page.homeMissionDescription"
          :items="page.homeMissionItems"
        />
      </section>
    </main>
  </TemplateBase>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { setupTranslationPrefixer } from '~/composables/util'
import { setupSiteConfig } from '~/composables/craft'
import { useVariantHomeDetailStore } from '~/store/variant-home-detail'

interface Props {
  page: {
    title: string
    subtitle: string
    image: string
    animalCategories: any
    // More precise types should be used
    highlightedAnimals: any[]
    bannerImage?: string
    bannerTitle?: string
    bannerText?: string
    buttonTitle?: string
    buttonUrl?: string
    secondaryButtonTitle?: string
    secondaryButtonUrl?: string
    misfitAnimals: any[]
    animalSuccesses: any[]
    animalSuccessTitle?: string
    animalSuccessSubtitle?: string
    successButtonTitle?: string
    successButtonUrl?: string
    homeMissionTitle?: string
    homeMissionEmbedTitle?: string
    homeMissionEmbed?: {
      url: string | null
      code: string | null
    }
    homeMissionDescription?: string
    homeMissionItems?: any[]
  }
}

defineProps<Props>()
const { site } = setupSiteConfig()

const animalCounts = computed(() => useVariantHomeDetailStore().animalCounts)
const { t_ } = setupTranslationPrefixer('VariantHomeDetail', site)
</script>

<style lang="scss" scoped>
.header-margin {
  margin-bottom: -6vw;

  @screen sm {
    margin-bottom: -17vw;
  }

  @screen lg {
    margin-bottom: -10vw;
  }

  @screen xl {
    margin-bottom: -17vw;
  }
}

.article-overview-container {
  @media screen and (min-width: calc(1280px + 96px)) {
    @apply px-0;

    .article-overview-container__molecule {
      @apply -mx-5;
    }
  }

  @screen 2xl {
    @apply px-56;

    .article-overview-container__molecule {
      @apply mx-0;
    }
  }

  @media screen and (min-width: calc(1750px + 96px)) {
    .article-overview-container__molecule {
      @apply -mx-12;
    }
  }
}
</style>
